import { useState, useEffect } from 'react'
import { GetServerSideProps } from 'next'

import { List } from '../../components/List/List'
import { ListItemCalendarTicket } from '../../components/List/ListItemCalendarTicket/ListItemCalendarTicket'
import { BestPriceGuarantee } from '../../components/Banners/BestPriceGuarantee/BestPriceGuarantee'
import { NumParksPicker } from '../../components/List/ListItemCalendarTicket/NumParksPicker/NumParksPicker'
import { FloatingCart } from '../../components/Cart/FloatingCart/FloatingCart'
import { ContinueLink } from '../../components/Misc/ContinueLink/ContinueLink'
import { TextApiResponseType, TextType } from '../../types/TextType'
import { PageView } from '../../components/Analytics/PageView/PageView'

import type { ProductType, ProductLeadPriceType, ProductLeadPriceDisplayType } from '../../types/ProductType'
import { BookingSiteOperated } from '../../components/Misc/BookingSiteOperated/BookingSiteOperated'
import { ExpressPassesComponent } from '../../components/Hotels/ExpressPassesComponent/ExpressPassComponent';
import Head from 'next/head';
import { getLeadPricing } from '../../utils/pricingUtils'

const textTypes = ['express_pass_page_intro']

/**
 * We don't have "number of parks" in the product data and it's probably
 * not worth adding, since the field wouldn't be relevant for 99% of our
 * products. We are using the hard coded ticket ids to filter.
 */
function getProductsForNumberOfParks(numParks: number, products: ProductType[]) {

  // Use Park Ids (589 for Universal Studios, 590 for Islands of Adventure and 336293 for Epic Universe to determine how many parks the pass is for)
  const parkIds = new Set(['589', '590', '336293']);

  return products.filter(p => {
    const matchCount = p.attractions.filter(attr => parkIds.has(attr.id)).length;
    return matchCount === numParks;
  });
}

function ExpressPass({ products, texts, productLeadPricing }: { products: ProductType[], texts: TextType[],  productLeadPricing: ProductLeadPriceType[] }) {

  const [ numParks, setNumParks ] = useState(1)
  const [ filteredProducts, setFilteredProducts ] = useState(getProductsForNumberOfParks(numParks, products))

  // Filter products by number of of parks when number of parks changes:
  useEffect(() => setFilteredProducts(getProductsForNumberOfParks(numParks, products)), [ numParks, products ])

  const title = "Express Passes"
  const introText = texts.find(t => t.text_type === 'express_pass_page_intro')?.body ?? '';

  const leadPricingData = getLeadPricing(productLeadPricing)

  return (
    <>
      <Head key={'express-passess'}><title>Universal Orlando™ | Express Passess</title></Head>
      <PageView
        pageName='Express Passes'
        productsOnPage={ products }
      />
      <main className='products'>
      <FloatingCart showNextStep={true} currentSection='expressPasses' placement="top" />
        <ContinueLink currentSection='expressPasses' />
        <List title={title} introText={introText} rating={''} expressPassesHotelsInclusions={<ExpressPassesComponent/>}>
          <BookingSiteOperated placement="intro" />
          <NumParksPicker numParks={numParks} setNumParks={setNumParks} />
          {filteredProducts.length > 0 ?
            filteredProducts.map((product: ProductType) => {
              // Find the lead price using the product id.
              const leadPriceDisplay: ProductLeadPriceDisplayType | undefined = leadPricingData.find(productLeadPrice => productLeadPrice.id == product.id)

              return (
                <ListItemCalendarTicket
                  key={product.id}
                  product={product}
                  isDatedTicket={false}
                  leadPrice={leadPriceDisplay}
                  isExpressTicket={true}
                />
              )
            })
            : <h2>No tickets available, please try again later.</h2>
          }
        </List>
        <FloatingCart showNextStep={true} currentSection='expressPasses' placement="bottom" />
      </main>
      <BestPriceGuarantee />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {

  // Get the ticket nids from the products API
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/products?limit=50`);
  const ticketData = await response.json();
  const ticketNids = ticketData.data.map((ticket: { id: string }) => ticket.id);

  // Get products:
  const fetches = ticketNids.map((ticketId: string) => fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/products/${ticketId}?view=website`))

  const results = await Promise.all(fetches)

  const products: Array<ProductType | null> = await Promise.all(results.map(result => {
    if (result.statusText === 'OK') {
      return result.json()
    } else {
      return null
    }
  }))

    // Get Lead Prices:
    const leadPriceFetch = ticketNids.map((ticketId: string) => fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/products/lead-price/${ticketId}`))
    const leadPriceResult = await Promise.all(leadPriceFetch);
    // Add each lead price result to Park Ticket props.
    const leadPrice: Array<ProductLeadPriceType | null> = await Promise.all(leadPriceResult.map(result => {
      if (result.statusText === 'OK') {
        return result.json()
      } else {
        return null
      }
    }))

  // Get page text:
  const textResult = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/text?types=${textTypes.join(",")}`)
  const texts: TextApiResponseType = await textResult.json()

  return { props: {
    // Filter products using Attraction Product Type id 8127 (Express)
    products: products.filter(p => ((p !== null) && (p.tags.some(tag => tag.id === '8127')))),
    texts: texts.data,
    productLeadPricing: leadPrice.filter(l => l !== null)
  }}
}

export default ExpressPass


